// src/modules/trial.js — Trial warning thresholds + pure banner-state logic + banner DOM updater.
// Named thresholds live only on the client; the backend emits subscriptionStatus + trialEndDate.
// getCachedTrialState is imported lazily inside updateTrialBanner so this module's pure logic
// (trialBannerState) stays importable by the node self-check without dragging in browser globals.

export const TRIAL_WARN_YELLOW_DAYS = 30;
export const TRIAL_WARN_RED_DAYS = 15;

const MS_PER_DAY = 86400000;

/**
 * Pure trial-banner threshold logic.
 * @param {string} status - subscription status ('subscribed' | 'trial' | ...)
 * @param {string|null} trialEndDate - ISO-8601 date string or null
 * @param {number} now - epoch ms (defaults to Date.now())
 * @returns {{level:'none'|'yellow'|'red'|'expired', daysLeft:number|null}}
 */
export function trialBannerState(status, trialEndDate, now = Date.now()) {
    if (status === 'subscribed') return { level: 'none', daysLeft: null };
    if (!trialEndDate) return { level: 'none', daysLeft: null };
    const end = Date.parse(trialEndDate);
    if (Number.isNaN(end)) return { level: 'none', daysLeft: null };
    const daysLeft = Math.ceil((end - now) / MS_PER_DAY);
    if (daysLeft <= 0) return { level: 'expired', daysLeft };
    if (daysLeft <= TRIAL_WARN_RED_DAYS) return { level: 'red', daysLeft };
    if (daysLeft <= TRIAL_WARN_YELLOW_DAYS) return { level: 'yellow', daysLeft };
    return { level: 'none', daysLeft };
}

function applyBanner(el, result) {
    if (!el) return;
    if (result.level === 'yellow') {
        el.className = 'alert alert-warning';
        el.textContent = result.daysLeft + ' days left in your trial';
        el.style.display = '';
    } else if (result.level === 'red') {
        el.className = 'alert alert-danger';
        el.textContent = result.daysLeft + ' days left in your trial';
        el.style.display = '';
    } else {
        el.style.display = 'none';
    }
}

/**
 * Read cached trial state and toggle both banner mounts (#trialBanner, #trialBannerPin).
 * Follows updateConsentVisibility's show/hide pattern.
 */
export function updateTrialBanner() {
    // Lazy import keeps trialBannerState importable by the node self-check (auth.js pulls browser globals).
    import('./auth.js').then(({ getCachedTrialState }) => {
        const trial = getCachedTrialState();
        const result = trialBannerState(trial.status, trial.trialEndDate);
        applyBanner(document.getElementById('trialBanner'), result);
        applyBanner(document.getElementById('trialBannerPin'), result);
    });
}
